import { Component, OnInit, OnChanges, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentService, BonusServiceData } from '@wellfit-tech/wf-plans-services';

@Component({
  selector: 'app-ancillary-benefits',
  templateUrl: './ancillary-benefits.component.html',
  styleUrls: ['./ancillary-benefits.component.scss']
})
export class AncillaryBenefitsComponent implements OnInit, OnChanges {
  @Input() ancillaryCodes: string = '';
  activeTab: string = 'dental';
  currentBonusServiceHtml: string = '';
  visibleTabs: { [key: string]: boolean } = {
    vision: false,
    lasik: false,
    hearing: false,
    prescriptions: false,
    urgentcare: false,
    therapy: false
  };

  private codeToSection: { [key: string]: string } = {
    // Vision
    'EYEM': 'vision',
    'VSCA': 'vision',
    'VSPS': 'vision',
    // Lasik
    'LCA': 'lasik',
    'AMST': 'lasik',
    // Hearing
    'HRPO': 'hearing',
    'BLTN': 'hearing',
    // Prescriptions
    'ENVC': 'prescriptions',
    'MEDI': 'prescriptions',
    'UNOA': 'prescriptions',
    'WELD': 'prescriptions',
    'QBLL': 'prescriptions',
    'PRFL': 'prescriptions',
    'NSDI': 'prescriptions',
    'BEN': 'prescriptions',
    'DLAO': 'prescriptions',
    'WDAV': 'prescriptions',
    // Urgent Care
    'DCCF': 'urgentcare',
    'LSRC': 'urgentcare',
    'WADCCF-40': 'urgentcare',
    'DCNC': 'urgentcare',
    'USIN': 'urgentcare',
    // Therapy
    'DMCF': 'therapy',
    'DMNC': 'therapy',
    'DMCF-70': 'therapy'
  };

  constructor(
    private route: ActivatedRoute, 
    private router: Router,
    private contentService: ContentService
  ) {}

  ngOnChanges() {
    this.updateVisibleTabs();
    this.setDefaultActiveTab();
    if (this.activeTab) {
      this.loadBonusServiceContent(this.activeTab);
    }
  }

  ngOnInit() {
    this.updateVisibleTabs();
    // Check if there's a fragment in the URL (e.g., #nav-vision)
    this.route.fragment.subscribe(fragment => {
      if (fragment) {
        // Map fragment to tab name
        const tabMap: { [key: string]: string } = {
          'nav-vision': 'vision',
          'nav-lasik': 'lasik',
          'nav-prescriptions': 'prescriptions',
          'nav-hearing': 'hearing',
          'nav-urgentcare': 'urgentcare',
          'nav-therapy': 'therapy'
        };
        const tab = tabMap[fragment];
        if (tab && this.visibleTabs[tab]) {
          this.activeTab = tab;
          this.loadBonusServiceContent(tab);
        }
      } else {
        this.setDefaultActiveTab();
        if (this.activeTab) {
          this.loadBonusServiceContent(this.activeTab);
        }
      }
    });
  }

  private updateVisibleTabs() {
    // Reset all to false
    Object.keys(this.visibleTabs).forEach(key => this.visibleTabs[key] = false);
    
    if (this.ancillaryCodes) {
      const codes = this.ancillaryCodes.split('|');
      codes.forEach(code => {
        const section = this.codeToSection[code.trim()];
        if (section) {
          this.visibleTabs[section] = true;
        }
      });
    }
  }

  private setDefaultActiveTab() {
    const visibleTabKeys = Object.keys(this.visibleTabs).filter(key => this.visibleTabs[key]);
    if (visibleTabKeys.length > 0) {
      this.activeTab = visibleTabKeys[0];
    } else {
      this.activeTab = '';
    }
  }

  switchTab(tab: string, fragment: string) {
    if (this.visibleTabs[tab]) {
      this.activeTab = tab;
      // Update the URL fragment without navigating
      this.router.navigate([], {
        fragment: fragment,
        relativeTo: this.route
      });
      // Load content for the active tab
      this.loadBonusServiceContent(tab);
    }
  }

  private loadBonusServiceContent(tab: string) {
    // Find the actual ancillary code for this tab section from the available codes
    const availableCodes = this.ancillaryCodes ? this.ancillaryCodes.split('|') : [];
    const codeForTab = availableCodes.find(code => {
      const section = this.codeToSection[code.trim()];
      return section === tab;
    });

    if (codeForTab) {
      this.contentService.getBonusServices(codeForTab.trim()).subscribe({
        next: (bonusServiceData: BonusServiceData) => {
          this.currentBonusServiceHtml = bonusServiceData.HtmlBodyPart?.Html || '';
        },
        error: (error) => {
          console.error('Error loading bonus service content:', error);
          this.currentBonusServiceHtml = '<p>Content not available at this time.</p>';
        }
      });
    }
  }
}